import { DataTypes, DataVariants } from "../../../data/DataManager";
import { GridFilterTypes } from "../../../grid/AgGridManager";
import { InputTypes } from "../../../form/FormManager";
import { EMPTY_VALUE, FieldSchema } from "../../../Schema";
import { Floor } from "../external-sources/Floors";
import { getAndProcessRooms, getRoom, Room } from "../external-sources/Rooms";
import { Site } from "../external-sources/Sites";
import { SchemaContext, KeyValueConstruct } from "../../common";
import { Modes } from "./mode";
import { LinkedInstance } from "../external-sources/LinkedInstances";
import { User } from "../external-sources/Users";

export const createRoom = (context: SchemaContext): FieldSchema => ({
  name: "room",
  grid: {
    displayed: true,
    order: 15,
    filter: GridFilterTypes.TEXT,
  },
  graphql: { selection: "room { key value }" },
  dataTransform: {
    fromGraphql: {
      getter: async ({ siteName, floor, room }) => {
        const siteId = context.sites.find((s) => s.siteName === siteName)?.siteId;
        const floorId = Number((floor as KeyValueConstruct)?.key);
        const roomId = Number((room as KeyValueConstruct)?.key);
        if (siteId && floorId && roomId) {
          const roomData = await getRoom(context.client, roomId);
          if (roomData && roomData.siteId === siteId && roomData.floorsId === floorId) {
            return { id: roomData.id, roomCode: roomData.roomCode, siteId: roomData.siteId, floorsId: roomData.floorsId };
          }
        }
        return EMPTY_VALUE;
      },
      fields: ["siteName", "buildingLocation", "floor"],
    },
    toGraphql: {
      getter: ({ room }) => {
        if (room) {
          return { key: (room as Room)?.id, value: (room as Room)?.roomCode };
        }
        return EMPTY_VALUE;
      },
    },
    // See buildingLocation: the stored key is text.
    toDb: {
      getter: ({ room }) => {
        if (room) {
          return { key: String((room as Room)?.id), value: (room as Room)?.roomCode };
        }
        return EMPTY_VALUE;
      },
    },
  },
  data: {
    type: DataTypes.OBJECT,
    variant: DataVariants.ENUM,
    options: {
      getter: async ({ siteName, buildingLocation, floor }) => {
        // The keys the query binds — see buildingLocation's options. buildingLocation stays
        // in the dependency list because a room list is only meaningful once a building has
        // been chosen, even though the query itself narrows by floor.
        const siteId = (siteName as Site)?.siteId;
        const floorId = (floor as Floor)?.id;
        if (!siteId || !buildingLocation || !floorId) {
          return [];
        }
        return (await getAndProcessRooms(context.client, siteId, floorId))
          .map((r: Room) => ({ value: r, label: r.roomCode }));
      },
      fields: ["siteName", "buildingLocation", "floor"],
    },
  },
  display: {
    displayed: true,
    translator: (value) => [(value as Room).roomCode as string],
  },
  validation: { required: true },
  form: {
    label: "Room",
    type: InputTypes.SELECT,
    disabled: {
      getter: ({ siteName, buildingLocation, floor, linkedInstance, mode, currentUser }) => {
        return !siteName || !buildingLocation || !floor ||
        (
          [Modes.EDIT, Modes.BULK_EDIT].includes(mode as Modes) &&
          ["P1S Uni-Directional"].includes((linkedInstance as LinkedInstance)?.linkedInstance)
        ) && !(currentUser as User)?.isAdmin;
      },
      fields: ["siteName", "buildingLocation", "floor", "linkedInstance", "mode", "currentUser"],
    },
  },
});
